/**
 * Safe wrapper around `window.Meticulous.context.*`.
 *
 * `window.Meticulous` is only present after the Meticulous recorder snippet
 * has loaded — currently gated to approved internal accounts in dev (see
 * `src/util/hooks/useMeticulousRecorder.ts`). Every method here is a
 * fire-and-forget no-op when the recorder is missing, and is guaranteed not
 * to throw, so call sites (viewer hooks, flag providers, etc.) don't need to
 * guard.
 *
 * The Meticulous docs state that optional chaining alone is sufficient, but
 * we follow the same defensive pattern as `services/analytics/Analytics.ts`
 * because a bare `window?.x` access still throws a `ReferenceError` if
 * `window` itself isn't declared, and because the Meticulous SDK could
 * theoretically throw on unexpected input.
 *
 * https://app.meticulous.ai/docs/how-to/record-session-context
 * https://app.meticulous.ai/docs/how-to/typescript-types
 */
export const Meticulous = {
  /**
   * Record the ID of the logged-in user. Subsequent calls overwrite.
   */
  recordUserId: (userId: string | undefined): void => {
    try {
      if (userId != null) {
        window.Meticulous?.context.recordUserId(userId);
      }
    } catch (err) {
      console.warn('Meticulous.recordUserId failed', err);
    }
  },

  /**
   * Record the email of the logged-in user. Subsequent calls overwrite.
   */
  recordUserEmail: (emailAddress: string | undefined): void => {
    try {
      if (emailAddress != null) {
        window.Meticulous?.context.recordUserEmail(emailAddress);
      }
    } catch (err) {
      console.warn('Meticulous.recordUserEmail failed', err);
    }
  },

  /**
   * Record arbitrary session context (account name, user role, theme, etc.).
   * Anything that changes how the UI looks or behaves between sessions is
   * worth recording so Meticulous can tell those differences apart from real
   * diffs. Subsequent calls with the same label overwrite.
   */
  recordCustomContext: (label: string, value: string | boolean): void => {
    try {
      window.Meticulous?.context.recordCustomContext(label, value);
    } catch (err) {
      console.warn('Meticulous.recordCustomContext failed', err);
    }
  },
};
